import { useEffect, useState, useCallback, useRef } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";
import { BookOpen, Building2, User, Loader2, Send, Trash2, Sparkles, Image as ImageIcon, RefreshCw, CalendarClock, Clock, X, ChevronLeft, ChevronRight, Copy, ExternalLink, Rss, Upload } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-amber-accent/10 text-amber-accent border-amber-accent/40",
  scheduled: "bg-signal-500/10 text-signal-500 border-signal-500/40",
  published: "bg-emerald-400/10 text-emerald-400 border-emerald-400/40",
  dismissed: "bg-muted text-muted-foreground border-border",
};

// Best B2B LinkedIn engagement windows — Tue & Thu mornings.
const RECOMMENDED_DAYS = [2, 4]; // JS getDay(): 0=Sun … 2=Tue, 4=Thu

function nextRecommendedSlot() {
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  d.setDate(d.getDate() + 1); // start from tomorrow
  for (let i = 0; i < 14 && !RECOMMENDED_DAYS.includes(d.getDay()); i++) d.setDate(d.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
}

function fmtWhen(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genBusy, setGenBusy] = useState(null);
  const [topic, setTopic] = useState("");
  const [contentType, setContentType] = useState("long_post");
  const [schedView, setSchedView] = useState("calendar");
  const [chips, setChips] = useState([
    "LCL consolidation", "FCL ocean freight", "Full truck loads (FTL)",
    "Customs clearance", "Bonded warehouses", "Project / oversized cargo",
    "North Adriatic advantage", "India → Koper corridor", "China → CEE corridor",
  ]);
  const [topicPlaceholder, setTopicPlaceholder] = useState("e.g. LCL consolidation Singapore → Koper, or bonded warehouse benefits");
  const [pageName, setPageName] = useState("your page");
  const [personName, setPersonName] = useState("Rok");
  const [blogConfigured, setBlogConfigured] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/articles");
      setArticles(data.articles || []);
    } catch { toast.error("Failed to load articles"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/articles/themes")
      .then(({ data }) => {
        if (data.themes?.length) setChips(data.themes);
        if (data.placeholder) setTopicPlaceholder(data.placeholder);
        if (data.page_name) setPageName(data.page_name);
        if (data.person_name) setPersonName(data.person_name);
        setBlogConfigured(!!data.blog_configured);
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const blogPush = async (aid) => {
    try {
      const { data } = await api.post(`/articles/${aid}/push-to-blog`);
      toast.success("Pushed to blog (SEO app)", { description: data.blog_url ? `Live at ${data.blog_url}` : "The site will rebuild shortly." });
      await load();
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(err === "blog_not_configured" ? "Add your blog endpoint + API key in Settings → Blog / SEO ingestion first." : (err || "Push to blog failed"));
    }
  };

  const reschedule = async (article, cellDate) => {
    const orig = article.scheduled_at ? new Date(article.scheduled_at) : new Date();
    const d = new Date(cellDate);
    d.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
    if (d.getTime() <= Date.now()) { toast.error("Pick a future day/time"); return; }
    try {
      const { data } = await api.post(`/articles/${article.id}/schedule`, { scheduled_at: d.toISOString() });
      toast.success(`Rescheduled to ${fmtWhen(data.scheduled_at)}`);
      await load();
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(err === "scheduled_time_in_past" ? "Pick a future day/time." : (err || "Could not reschedule"));
    }
  };

  const generate = async (cadence) => {
    setGenBusy(cadence);
    try {
      await api.post("/articles/generate", { cadence, topic: topic.trim() || undefined, content_type: contentType });
      const kindLabel = contentType === "article" ? "Article" : "Long post";
      toast.success(`${kindLabel} drafted (${cadence === "weekly" ? "as page" : "as Rok"})`, {
        description: topic.trim()
          ? `About: ${topic.trim()}`
          : (cadence === "weekly" ? "Written for the Europacific page." : "Written in Rok's voice."),
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    }
    setGenBusy(null);
  };

  const drafts = articles.filter((a) => a.status === "draft");
  const scheduled = articles
    .filter((a) => a.status === "scheduled")
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
  const history = articles.filter((a) => !["draft", "scheduled", "publishing"].includes(a.status));

  return (
    <DashboardLayout eyebrow="Content" title="LinkedIn Articles"
      actions={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => generate("monthly")} disabled={genBusy} data-testid="gen-monthly-btn">
            {genBusy === "monthly" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <User className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Monthly draft (as {personName})</span>
          </Button>
          <Button size="sm" onClick={() => generate("weekly")} disabled={genBusy} data-testid="gen-weekly-btn">
            {genBusy === "weekly" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Building2 className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Weekly draft (as page)</span>
          </Button>
        </div>
      }>
      <p className="text-sm text-muted-foreground mb-4 max-w-3xl">
        AI-drafted long-form articles built from the trending posts of the pages & people you follow.
        <strong> Weekly</strong> articles publish as the <strong>{pageName} page</strong>;
        <strong> monthly</strong> ones as <strong>{personName}</strong>. New drafts are auto-created on schedule —
        review, edit and publish below. Drafts publish as a rich long-form LinkedIn post.
      </p>

      {/* Topic composer — steer what the next draft is about */}
      <div className="data-card mb-6" data-testid="article-topic-composer">
        <div className="mb-3">
          <label className="label-eyebrow block mb-1.5">Content type</label>
          <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="content-type-toggle">
            <button type="button" data-testid="content-type-long_post"
              onClick={() => setContentType("long_post")}
              className={`text-xs px-3 py-1.5 transition-colors ${contentType === "long_post" ? "bg-signal-500 text-white" : "text-muted-foreground hover:bg-muted/40"}`}>
              Long post
            </button>
            <button type="button" data-testid="content-type-article"
              onClick={() => setContentType("article")}
              className={`text-xs px-3 py-1.5 transition-colors border-l border-border ${contentType === "article" ? "bg-signal-500 text-white" : "text-muted-foreground hover:bg-muted/40"}`}>
              Article
            </button>
          </div>
          <p className="text-[0.7rem] text-muted-foreground mt-1.5">
            {contentType === "long_post"
              ? "Long-form feed post — auto-published to LinkedIn via the app."
              : "Native LinkedIn Article — you publish it in LinkedIn's Article editor, paste the URL back, and the app auto-posts a short promo post linking to it."}
          </p>
        </div>
        <label className="label-eyebrow block mb-1.5">What should the next {contentType === "article" ? "article" : "post"} be about? <span className="text-muted-foreground">(optional — leave blank to use trending themes)</span></label>
        <Input
          data-testid="article-topic-input"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder={topicPlaceholder}
          className="mb-2"
        />
        <div className="flex flex-wrap gap-1.5">
          {chips.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`article-topic-chip-${t}`}
              onClick={() => setTopic(t)}
              className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${topic === t ? "border-signal-500 bg-signal-500/10 text-signal-500" : "border-border text-muted-foreground hover:border-foreground/40"}`}
            >
              {t}
            </button>
          ))}
          {topic && (
            <button type="button" data-testid="article-topic-clear" onClick={() => setTopic("")}
              className="text-xs px-2.5 py-1 rounded-sm border border-border text-muted-foreground hover:text-destructive inline-flex items-center gap-1">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="data-card text-center py-12 text-sm text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
        </div>
      ) : (
        <>
          {drafts.length === 0 && (
            <div className="data-card text-center py-12 mb-6" data-testid="articles-empty">
              <BookOpen className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <h3 className="font-display font-semibold">No drafts yet</h3>
              <p className="text-xs text-muted-foreground mt-1">Generate one now, or wait for the next scheduled weekly/monthly draft.</p>
            </div>
          )}
          <div className="space-y-4">
            {drafts.map((a) => <ArticleCard key={a.id} article={a} onChanged={load} blogConfigured={blogConfigured} personName={personName} />)}
          </div>

          {scheduled.length > 0 && (
            <div className="mt-10">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" /> Scheduled ({scheduled.length})
                </div>
                <div className="flex items-center gap-1 text-[0.65rem]">
                  <button
                    onClick={() => setSchedView("calendar")}
                    className={`px-2.5 py-1 rounded-sm border ${schedView === "calendar" ? "bg-signal-500/10 text-signal-500 border-signal-500/40" : "border-border text-muted-foreground hover:text-foreground"}`}
                    data-testid="sched-view-calendar"
                  >Calendar</button>
                  <button
                    onClick={() => setSchedView("list")}
                    className={`px-2.5 py-1 rounded-sm border ${schedView === "list" ? "bg-signal-500/10 text-signal-500 border-signal-500/40" : "border-border text-muted-foreground hover:text-foreground"}`}
                    data-testid="sched-view-list"
                  >List</button>
                </div>
              </div>
              {schedView === "calendar" ? (
                <ScheduleCalendar articles={scheduled} onReschedule={reschedule} />
              ) : (
                <div className="space-y-2">
                  {scheduled.map((a) => <ScheduledCard key={a.id} article={a} onChanged={load} />)}
                </div>
              )}
            </div>
          )}

          {history.length > 0 && (
            <div className="mt-10">
              <div className="label-eyebrow mb-3">History</div>
              <div className="space-y-2">
                {history.map((a) => {
                  const kind = a.content_type === "article" ? "Article" : (a.content_type === "post" ? (a.promotes_article_id ? "Promo post" : "Post") : "Long post");
                  return (
                  <div key={a.id} className="data-card flex items-center justify-between gap-3" data-testid={`article-history-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {a.title}
                        {a.article_url && (
                          <a href={a.article_url} target="_blank" rel="noreferrer" className="text-signal-500 hover:underline inline-flex items-center gap-0.5 text-[0.65rem] shrink-0" data-testid={`article-link-${a.id}`}>
                            <ExternalLink className="w-3 h-3" /> view
                          </a>
                        )}
                      </div>
                      <div className="text-[0.65rem] text-muted-foreground">
                        <span className="uppercase tracking-wide text-amber-accent">{kind}</span>
                        {" · "}{a.cadence} · {(a.posted_as || a.identity) === "personal" ? "as Rok" : "as page"}
                        {a.published_at ? ` · published ${relativeTime(a.published_at)}` : ""}
                        {a.blog_status === "published" && (
                          <span className="text-emerald-400"> · blog ✓{a.blog_url ? "" : ""}</span>
                        )}
                        {a.blog_status === "failed" && <span className="text-destructive"> · blog push failed</span>}
                        {a.blog_url && (
                          <a href={a.blog_url} target="_blank" rel="noreferrer" className="text-signal-500 hover:underline inline-flex items-center gap-0.5 ml-1" data-testid={`blog-link-${a.id}`}>
                            <ExternalLink className="w-3 h-3" /> blog
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {(a.content_type === "article" || a.content_type === "long_post") && (
                        <Button size="sm" variant="ghost" disabled={!blogConfigured} title={blogConfigured ? "Push to your blog" : "No blog connected for this workspace — set one up in Settings"} className="h-7 px-2 text-[0.65rem] rounded-sm disabled:opacity-40" onClick={() => blogPush(a.id)} data-testid={`history-push-blog-${a.id}`}>
                          <Rss className="w-3 h-3 mr-1" />{a.blog_status === "published" ? "Re-push" : "Push to blog"}
                        </Button>
                      )}
                      <span className={`text-[0.6rem] px-2 py-0.5 rounded-sm border ${STATUS_STYLE[a.status] || ""}`}>{a.status}</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}

function ArticleCard({ article, onChanged, blogConfigured = true, personName = "Rok" }) {
  const [title, setTitle] = useState(article.title);
  const [body, setBody] = useState(article.body);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(nextRecommendedSlot());
  const [imgUrl, setImgUrl] = useState(null);
  const [imgGenerating, setImgGenerating] = useState(article.image_status === "generating");
  const [imgGuidance, setImgGuidance] = useState(article.image_guidance || "");
  const imgFileRef = useRef(null);
  const [artUrl, setArtUrl] = useState("");
  const isArticle = article.content_type === "article";
  const dirty = title !== article.title || body !== article.body;

  useEffect(() => {
    let active = true;
    let timer;
    const fetchImg = async () => {
      try {
        const res = await api.get(`/articles/${article.id}/image`, { responseType: "blob" });
        if (active) { setImgUrl(URL.createObjectURL(res.data)); setImgGenerating(false); }
        return true;
      } catch { return false; }
    };
    (async () => {
      if (article.image_status === "ready") { await fetchImg(); return; }
      if (article.image_status === "generating") {
        setImgGenerating(true);
        let n = 0;
        timer = setInterval(async () => {
          n += 1;
          const ok = await fetchImg();
          if (ok || n > 24) { clearInterval(timer); if (!ok && n > 24) setImgGenerating(false); }
        }, 5000);
      }
    })();
    return () => { active = false; if (timer) clearInterval(timer); if (imgUrl) URL.revokeObjectURL(imgUrl); };
  }, [article.id, article.image_status]);

  const regenImage = async () => {
    setImgGenerating(true); setImgUrl(null);
    try {
      await api.post(`/articles/${article.id}/regenerate-image`, { image_guidance: imgGuidance.trim() });
      let n = 0;
      const t = setInterval(async () => {
        n += 1;
        try {
          const r = await api.get(`/articles/${article.id}/image`, { responseType: "blob" });
          setImgUrl(URL.createObjectURL(r.data)); setImgGenerating(false); clearInterval(t);
        } catch { if (n > 24) { clearInterval(t); setImgGenerating(false); } }
      }, 5000);
    } catch { toast.error("Could not regenerate image"); setImgGenerating(false); }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)"); return; }
    setImgGenerating(true); setImgUrl(null);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/articles/${article.id}/upload-image`, { image_base64: reader.result });
        const r = await api.get(`/articles/${article.id}/image`, { responseType: "blob", params: { t: Date.now() } });
        setImgUrl(URL.createObjectURL(r.data)); setImgGenerating(false);
        toast.success("Photo uploaded");
      } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); setImgGenerating(false); }
    };
    reader.onerror = () => { toast.error("Could not read file"); setImgGenerating(false); };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/articles/${article.id}`, { title, body }); toast.success("Saved"); await onChanged(); }
    catch { toast.error("Save failed"); }
    setBusy(false);
  };
  const publish = async () => {
    if (dirty) await save();
    setConfirming(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/articles/${article.id}/publish`);
      toast.success(data.identity === "page" ? "Published as the Europacific page" : "Published as Rok");
      await onChanged();
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      const friendly =
        err === "invalid_title" ? "Give the article a real title before publishing (this one looks like a placeholder)."
        : err === "no_company_page_configured" ? "Set the Europacific company page in Page Engagement first."
        : err === "no_connected_linkedin_account" ? "Connect a LinkedIn account first."
        : /too many characters|too long/i.test(err) ? "Draft is too long for LinkedIn — trim it and try again."
        : (err || "Publish failed");
      toast.error(friendly);
    }
    setBusy(false);
  };
  const dismiss = async () => {
    setBusy(true);
    try { await api.post(`/articles/${article.id}/dismiss`); toast.success("Dismissed"); await onChanged(); }
    catch { toast.error("Failed"); }
    setBusy(false);
  };
  const doSchedule = async () => {
    if (!scheduleAt) { toast.error("Pick a date & time"); return; }
    const iso = new Date(scheduleAt).toISOString();
    if (new Date(scheduleAt).getTime() <= Date.now()) { toast.error("Pick a time in the future"); return; }
    if (dirty) await save();
    setBusy(true);
    try {
      const { data } = await api.post(`/articles/${article.id}/schedule`, { scheduled_at: iso });
      toast.success(`Scheduled for ${fmtWhen(data.scheduled_at)}`);
      setScheduling(false);
      await onChanged();
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      const friendly =
        err === "invalid_title" ? "Give the article a real title before scheduling."
        : err === "no_company_page_configured" ? "Set the Europacific company page in Page Engagement first."
        : err === "no_connected_linkedin_account" ? "Connect a LinkedIn account first."
        : err === "scheduled_time_in_past" ? "Pick a time in the future."
        : err === "invalid_datetime" ? "That date/time looks invalid."
        : (err || "Could not schedule");
      toast.error(friendly);
    }
    setBusy(false);
  };

  const isPage = article.identity === "page";
  const pushToBlog = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/articles/${article.id}/push-to-blog`);
      toast.success("Pushed to blog (SEO app)", {
        description: data.blog_url ? `Live at ${data.blog_url}` : "The site will rebuild shortly.",
      });
      await onChanged();
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(err === "blog_not_configured"
        ? "Add your blog endpoint + API key in Settings → Blog / SEO ingestion first."
        : (err || "Push to blog failed"));
    }
    setBusy(false);
  };
  const copyForArticle = async () => {
    const tags = (article.hashtags || []).join(" ");
    const text = `${title}\n\n${body}${tags ? `\n\n${tags}` : ""}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied — paste into LinkedIn's Article editor", {
        description: "Title on the first line, then the body. Add your cover image there.",
      });
    } catch { toast.error("Couldn't copy — select the text manually"); }
  };
  const publishNative = async () => {
    if (dirty) await save();
    setBusy(true);
    try {
      const { data } = await api.post(`/articles/${article.id}/publish-native`, { article_url: artUrl.trim() });
      if (data.promo_published) {
        toast.success("Article marked published + promo post posted", {
          description: data.identity === "page" ? "Promo posted as the Europacific page." : "Promo posted as Rok.",
        });
      } else {
        toast.warning("Article saved — but the promo post didn't post", {
          description: data.promo_error === "no_company_page_configured" ? "Set the Europacific page in Page Engagement, then post the promo manually."
            : data.promo_error === "no_connected_linkedin_account" ? "Connect a LinkedIn account to auto-post the promo."
            : `Reason: ${data.promo_error || "unknown"}`,
          duration: 10000,
        });
      }
      await onChanged();
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(err === "invalid_article_url" ? "Paste the full published LinkedIn article URL (starts with https://…linkedin.com/…)." : (err || "Failed"));
    }
    setBusy(false);
  };
  const titleLooksBad = (t) => {
    const s = (t || "").trim();
    return s.length < 4 || /\b(test[_\s-]?e2e|test|verification|lorem|ipsum|placeholder|untitled|dummy|sample)\b/i.test(s);
  };
  return (
    <div className="data-card" data-testid={`article-${article.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[0.6rem] px-2 py-0.5 rounded-sm border inline-flex items-center gap-1.5 ${isPage ? "bg-signal-500/10 text-signal-500 border-signal-500/40" : "border-border text-muted-foreground"}`}>
            {isPage ? <Building2 className="w-3 h-3" /> : <User className="w-3 h-3" />}
            {article.cadence} · publishes {isPage ? "as Europacific page" : "as Rok"}
          </span>
          <span className={`text-[0.6rem] px-2 py-0.5 rounded-sm border uppercase tracking-wide ${isArticle ? "bg-amber-accent/10 text-amber-accent border-amber-accent/40" : "border-border text-muted-foreground"}`}>
            {isArticle ? "Article" : "Long post"}
          </span>
        </div>
        <span className="text-[0.6rem] text-muted-foreground inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> from {article.source_count || 0} trending posts · {relativeTime(article.created_at)}
        </span>
      </div>

      {/* Header image */}
      <div className="mb-3 rounded-sm overflow-hidden border border-border bg-muted/30 aspect-[16/9] flex items-center justify-center relative">
        {imgUrl ? (
          <img src={imgUrl} alt="" className="w-full h-full object-cover" data-testid={`article-image-${article.id}`} />
        ) : imgGenerating ? (
          <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Generating header image…</div>
        ) : (
          <div className="text-xs text-muted-foreground flex items-center gap-2"><ImageIcon className="w-4 h-4" /> No header image</div>
        )}
        {!imgGenerating && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
            <button onClick={() => imgFileRef.current?.click()} data-testid={`article-upload-image-${article.id}`}
              className="text-[0.6rem] bg-background/80 backdrop-blur px-2 py-1 rounded-sm border border-border inline-flex items-center gap-1 hover:text-foreground">
              <Upload className="w-3 h-3" /> Upload photo
            </button>
            <button onClick={regenImage} data-testid={`article-regen-image-${article.id}`}
              className="text-[0.6rem] bg-background/80 backdrop-blur px-2 py-1 rounded-sm border border-border inline-flex items-center gap-1 hover:text-foreground">
              <RefreshCw className="w-3 h-3" /> {imgUrl ? "Regenerate" : "Generate image"}
            </button>
          </div>
        )}
        <input ref={imgFileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid={`article-image-file-${article.id}`} />
      </div>

      {/* Image guidance — steer what the header image depicts before regenerating */}
      <div className="mb-3 flex flex-col sm:flex-row gap-2 sm:items-center" data-testid={`article-img-guide-wrap-${article.id}`}>
        <Input
          value={imgGuidance}
          onChange={(e) => setImgGuidance(e.target.value)}
          placeholder="Describe the image you want (e.g. bonded warehouse interior with forklifts, no ship)"
          className="text-xs flex-1"
          data-testid={`article-img-guidance-${article.id}`}
        />
        <Button size="sm" variant="outline" onClick={regenImage} disabled={imgGenerating}
          data-testid={`article-regen-with-guidance-${article.id}`} className="rounded-sm shrink-0">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {imgUrl ? "Regenerate image" : "Generate image"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => imgFileRef.current?.click()} disabled={imgGenerating}
          data-testid={`article-upload-image-btn-${article.id}`} className="rounded-sm shrink-0">
          <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload photo
        </Button>
      </div>

      <Input value={title} onChange={(e) => setTitle(e.target.value)} className="font-display font-semibold mb-2" data-testid={`article-title-${article.id}`} />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="text-sm leading-relaxed mb-2" data-testid={`article-body-${article.id}`} />
      {article.hashtags?.length > 0 && (
        <div className="text-xs text-signal-500 mb-3">{article.hashtags.join(" ")}</div>
      )}
      {article.last_error && <p className="text-xs text-destructive mb-2">Last attempt: {article.last_error}</p>}

      {confirming ? (
        <div className="rounded-sm border border-signal-500/40 bg-signal-500/5 p-4 space-y-3" data-testid={`article-confirm-${article.id}`}>
          <div className="flex items-center gap-2 text-sm font-medium">
            {isPage ? <Building2 className="w-4 h-4 text-signal-500" /> : <User className="w-4 h-4 text-signal-500" />}
            Publish this article {isPage ? "as the Europacific page" : "as Rok (personal)"}?
          </div>
          <div>
            <div className="label-eyebrow mb-1">Title</div>
            <div className="text-sm font-display font-semibold">{title?.trim() || "(no title)"}</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Preview</div>
            <div className="text-xs text-muted-foreground leading-relaxed">{(body || "").slice(0, 180)}{(body || "").length > 180 ? "…" : ""}</div>
          </div>
          {titleLooksBad(title) && (
            <div className="text-xs text-destructive" data-testid={`article-confirm-badtitle-${article.id}`}>
              This title looks like a placeholder — edit it before publishing.
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={publish} disabled={busy || titleLooksBad(title)} data-testid={`article-confirm-publish-${article.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span className="ml-1.5">Confirm & publish {isPage ? "as page" : "as Rok"}</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={busy} data-testid={`article-confirm-cancel-${article.id}`}>Cancel</Button>
          </div>
        </div>
      ) : scheduling ? (
        <div className="rounded-sm border border-signal-500/40 bg-signal-500/5 p-4 space-y-3" data-testid={`article-schedule-${article.id}`}>
          <div className="flex items-center gap-2 text-sm font-medium">
            <CalendarClock className="w-4 h-4 text-signal-500" />
            Schedule to auto-publish {isPage ? "as the Europacific page" : "as Rok"}
          </div>
          <div>
            <div className="label-eyebrow mb-1">Publish date & time</div>
            <input
              type="datetime-local"
              value={scheduleAt}
              min={defaultScheduleLocal(0)}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="bg-input border border-border rounded-sm px-3 py-2 text-sm"
              data-testid={`article-schedule-input-${article.id}`}
            />
          </div>
          {titleLooksBad(title) && (
            <div className="text-xs text-destructive">This title looks like a placeholder — edit it before scheduling.</div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={doSchedule} disabled={busy || titleLooksBad(title)} data-testid={`article-schedule-confirm-${article.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />}
              <span className="ml-1.5">Schedule</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setScheduling(false)} disabled={busy} data-testid={`article-schedule-cancel-${article.id}`}>Cancel</Button>
          </div>
        </div>
      ) : (
        isArticle ? (
        <div className="rounded-sm border border-amber-accent/40 bg-amber-accent/5 p-4 space-y-3" data-testid={`article-native-flow-${article.id}`}>
          <div className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Publish as a native LinkedIn Article:</strong> LinkedIn doesn't allow
            auto-posting Articles, so: 1) copy the text, 2) paste it into LinkedIn's Article editor
            ({isPage ? "as the Europacific page" : "as Rok"}) & publish, 3) paste the live URL below —
            the app auto-posts a short promo post linking to it.
          </div>
          <Button size="sm" variant="outline" onClick={copyForArticle} disabled={busy || titleLooksBad(title)}
            data-testid={`article-copy-${article.id}`} className="rounded-sm">
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy for LinkedIn Article
          </Button>
          <div>
            <div className="label-eyebrow mb-1">Published Article URL</div>
            <Input value={artUrl} onChange={(e) => setArtUrl(e.target.value)}
              placeholder="https://www.linkedin.com/pulse/…"
              className="text-sm" data-testid={`article-url-input-${article.id}`} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={publishNative} disabled={busy || !artUrl.trim() || titleLooksBad(title)}
              data-testid={`article-publish-native-${article.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span className="ml-1.5">Mark published & post promo</span>
            </Button>
            {dirty && <Button size="sm" variant="outline" onClick={save} disabled={busy} data-testid={`article-save-${article.id}`}>Save edits</Button>}
            <Button size="sm" variant="outline" onClick={pushToBlog} disabled={busy || !blogConfigured} title={blogConfigured ? "Push to your blog" : "No blog connected for this workspace — set one up in Settings"} className="rounded-sm disabled:opacity-40" data-testid={`article-push-blog-${article.id}`}>
              <Rss className="w-3.5 h-3.5 mr-1.5" /> Push to blog
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} disabled={busy} data-testid={`article-dismiss-${article.id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Dismiss
            </Button>
          </div>
        </div>
        ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setConfirming(true)} disabled={busy} data-testid={`article-publish-${article.id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Publish {isPage ? "as page" : `as ${personName}`}</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => setScheduling(true)} disabled={busy} data-testid={`article-schedule-btn-${article.id}`}>
            <CalendarClock className="w-3.5 h-3.5 mr-1.5" /> Schedule
          </Button>
          <Button size="sm" variant="outline" onClick={pushToBlog} disabled={busy || !blogConfigured} title={blogConfigured ? "Push to your blog" : "No blog connected for this workspace — set one up in Settings"} className="rounded-sm disabled:opacity-40" data-testid={`article-push-blog-${article.id}`}>
            <Rss className="w-3.5 h-3.5 mr-1.5" /> Push to blog
          </Button>
          {dirty && <Button size="sm" variant="outline" onClick={save} disabled={busy} data-testid={`article-save-${article.id}`}>Save edits</Button>}
          <Button size="sm" variant="ghost" onClick={dismiss} disabled={busy} data-testid={`article-dismiss-${article.id}`}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Dismiss
          </Button>
        </div>
        )
      )}
    </div>
  );
}

function defaultScheduleLocal(addDays = 1) {
  const d = new Date();
  d.setDate(d.getDate() + addDays);
  if (addDays > 0) d.setHours(9, 0, 0, 0);
  // format to local "YYYY-MM-DDTHH:mm" for datetime-local input
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ScheduledCard({ article, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isPage = article.identity === "page";
  const unschedule = async () => {
    setBusy(true);
    try { await api.post(`/articles/${article.id}/unschedule`); toast.success("Moved back to drafts"); await onChanged(); }
    catch { toast.error("Failed"); }
    setBusy(false);
  };
  const publishNow = async () => {
    setBusy(true);
    try {
      await api.post(`/articles/${article.id}/publish`);
      toast.success(isPage ? "Published as the Europacific page" : "Published as Rok");
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
    setBusy(false);
  };
  return (
    <div className="data-card flex items-center justify-between gap-3" data-testid={`article-scheduled-${article.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{article.title}</div>
        <div className="text-[0.65rem] text-muted-foreground inline-flex items-center gap-1.5 mt-0.5">
          {isPage ? <Building2 className="w-3 h-3" /> : <User className="w-3 h-3" />}
          {article.cadence} · {isPage ? "as page" : "as Rok"}
          <span className="text-signal-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtWhen(article.scheduled_at)}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button size="sm" variant="outline" onClick={publishNow} disabled={busy} data-testid={`article-publishnow-${article.id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          <span className="ml-1.5">Publish now</span>
        </Button>
        <Button size="sm" variant="ghost" onClick={unschedule} disabled={busy} data-testid={`article-unschedule-${article.id}`}>
          <X className="w-3.5 h-3.5 mr-1" /> Unschedule
        </Button>
      </div>
    </div>
  );
}


function dayKey(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function ScheduleCalendar({ articles, onReschedule }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [dragId, setDragId] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const year = month.getFullYear();
  const mon = month.getMonth();
  const startWeekday = (new Date(year, mon, 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, mon + 1, 0).getDate();
  const todayKey = dayKey(new Date());
  const todayMidnight = new Date(new Date().toDateString());

  const byDay = {};
  articles.forEach((a) => {
    if (!a.scheduled_at) return;
    const k = dayKey(new Date(a.scheduled_at));
    (byDay[k] = byDay[k] || []).push(a);
  });

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, mon, d));

  const drop = (cellDate) => {
    const a = articles.find((x) => x.id === dragId);
    setDragId(null); setOverKey(null);
    if (a && cellDate) onReschedule(a, cellDate);
  };

  return (
    <div className="data-card" data-testid="schedule-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonth(new Date(year, mon - 1, 1))} className="p-1.5 rounded-sm border border-border hover:text-foreground text-muted-foreground" data-testid="cal-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="font-display font-semibold text-sm" data-testid="cal-month-label">
          {month.toLocaleString([], { month: "long", year: "numeric" })}
        </div>
        <button onClick={() => setMonth(new Date(year, mon + 1, 1))} className="p-1.5 rounded-sm border border-border hover:text-foreground text-muted-foreground" data-testid="cal-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[0.6rem] text-muted-foreground mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) => {
          if (!cell) return <div key={`b${i}`} />;
          const k = dayKey(cell);
          const past = cell < todayMidnight;
          const items = byDay[k] || [];
          const recommended = !past && RECOMMENDED_DAYS.includes(cell.getDay());
          return (
            <div
              key={k}
              onDragOver={(e) => { if (!past && dragId) { e.preventDefault(); setOverKey(k); } }}
              onDragLeave={() => setOverKey((cur) => (cur === k ? null : cur))}
              onDrop={(e) => { e.preventDefault(); if (!past) drop(cell); }}
              className={`min-h-[68px] rounded-sm border p-1 transition-colors ${past ? "opacity-40" : ""} ${overKey === k ? "border-signal-500 bg-signal-500/10" : recommended ? "border-signal-500/25 bg-signal-500/[0.05]" : "border-border"}`}
              data-testid={`cal-day-${k}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[0.6rem] ${k === todayKey ? "text-signal-500 font-semibold" : "text-muted-foreground"}`}>{cell.getDate()}</span>
                {recommended && <span className="text-[0.55rem] text-signal-500/70" title="Recommended posting window (peak B2B reach)" data-testid={`cal-recommended-${k}`}>★</span>}
              </div>
              {items.map((a) => (
                <div
                  key={a.id}
                  draggable
                  onDragStart={() => setDragId(a.id)}
                  onDragEnd={() => { setDragId(null); setOverKey(null); }}
                  title={`${a.title} · ${fmtTime(a.scheduled_at)}`}
                  className="mb-1 rounded-sm bg-signal-500/15 border border-signal-500/30 px-1 py-0.5 cursor-grab active:cursor-grabbing"
                  data-testid={`cal-event-${a.id}`}
                >
                  <div className="flex items-center gap-1">
                    {a.identity === "page" ? <Building2 className="w-2.5 h-2.5 shrink-0" /> : <User className="w-2.5 h-2.5 shrink-0" />}
                    <span className="text-[0.58rem] leading-tight truncate flex-1">{a.title}</span>
                  </div>
                  <div className="text-[0.52rem] text-signal-500">{fmtTime(a.scheduled_at)}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[0.6rem] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="text-signal-500/70">★</span> Recommended (Tue/Thu mornings — peak B2B reach)</span>
        <span>Drag an article to another day to reschedule (keeps the same time). Switch to List view to publish now or unschedule.</span>
      </div>
    </div>
  );
}
